import React from 'react';
import { Select, Button } from 'antd';
import { FiChevronLeft, FiChevronRight, FiChevronDown, FiCopy, FiDownload } from 'react-icons/fi';
import { InPageSpinner } from '@components/spinners/in-page-spinner';
import { AirflowTaskInstance } from '../../types';
import { FILTER_ALL, useLogsTab } from './use-logs-tab';
import styles from '../../styles.module.scss';

const { Option } = Select;

type LogsTabProps = {
    dagId: string;
    runId: string;
    taskId: string;
    taskInstance: AirflowTaskInstance | null;
    mapIndex: number;
};

const LEVEL_COLOR: Record<string, string> = {
    info: '#08c46a',
    warning: '#fcad01',
    warn: '#fcad01',
    error: '#ff4d4f',
    critical: '#ff4d4f',
    debug: '#1890ff',
};

/*
 * Formats an ISO timestamp compactly as "YYYY-MM-DD HH:MM:SS"
 */
function formatTimestamp(iso?: string): string {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/*
 * DAG-run-task detail's Logs tab — try-number navigation, level/source filters, group
 * collapse, and copy/download, backed by the shared log-parser
 */
export function LogsTab({ dagId, runId, taskId, taskInstance, mapIndex }: LogsTabProps) {
    const {
        selectedTry,
        maxTry,
        isLoading,
        levelFilter,
        setLevelFilter,
        sourceFilter,
        setSourceFilter,
        levelOptions,
        sourceOptions,
        visibleItems,
        handleToggleGroup,
        handlePrevTry,
        handleNextTry,
        handleCopy,
        handleDownload,
    } = useLogsTab(dagId, runId, taskId, taskInstance, mapIndex);

    return (
        <div className={styles.card}>
            <div className={styles.filterRow}>
                {maxTry > 1 && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Button size="small" disabled={selectedTry <= 1} onClick={handlePrevTry}>
                            <FiChevronLeft />
                        </Button>
                        <span style={{ color: '#666a8d', fontSize: 13 }}>{`Try ${selectedTry} of ${maxTry}`}</span>
                        <Button size="small" disabled={selectedTry >= maxTry} onClick={handleNextTry}>
                            <FiChevronRight />
                        </Button>
                    </span>
                )}
                <Select value={levelFilter} onChange={setLevelFilter} style={{ width: 130 }}>
                    <Option value={FILTER_ALL}>All Levels</Option>
                    {levelOptions.map((level) => (
                        <Option key={level} value={level}>
                            {level}
                        </Option>
                    ))}
                </Select>
                <Select value={sourceFilter} onChange={setSourceFilter} style={{ width: 150 }}>
                    <Option value={FILTER_ALL}>All Sources</Option>
                    {sourceOptions.map((source) => (
                        <Option key={source} value={source}>
                            {source}
                        </Option>
                    ))}
                </Select>
                <div className={styles.filterRowEnd}>
                    <Button size="small" onClick={handleCopy}>
                        <FiCopy style={{ marginRight: 6 }} />
                        Copy
                    </Button>
                    <Button size="small" onClick={handleDownload}>
                        <FiDownload style={{ marginRight: 6 }} />
                        Download
                    </Button>
                </div>
            </div>
            {isLoading ? (
                <InPageSpinner size="Small" />
            ) : (
                <div className={styles.codeViewer} style={{ padding: '4px 0' }}>
                    {visibleItems.map((row, index) =>
                        row.kind === 'group-header' ? (
                            <div
                                key={`group-${row.groupId}`}
                                onClick={() => handleToggleGroup(row.groupId)}
                                className={styles.logGroupToggle}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    fontSize: 12,
                                    padding: '4px 8px',
                                    paddingLeft: 8 + row.indent * 16,
                                }}
                            >
                                <FiChevronDown style={{ transform: row.isOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s' }} />
                                {row.name}
                            </div>
                        ) : (
                            // eslint-disable-next-line react/no-array-index-key
                            <div key={`line-${index}`} style={{ display: 'flex', gap: 8, padding: '1px 8px', paddingLeft: 8 + row.indent * 16, fontSize: 12, fontFamily: 'Consolas, Menlo, monospace' }}>
                                <span style={{ color: '#666a8d', width: 32, textAlign: 'right', flexShrink: 0 }}>{row.lineNumber}</span>
                                <span style={{ color: '#666a8d', flexShrink: 0 }}>{formatTimestamp(row.entry.timestamp)}</span>
                                {row.entry.level && <span style={{ color: LEVEL_COLOR[row.entry.level.toLowerCase()] ?? '#666a8d', flexShrink: 0 }}>{row.entry.level.toUpperCase()}</span>}
                                <span style={{ color: '#e1e1e6', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{row.entry.event}</span>
                            </div>
                        )
                    )}
                </div>
            )}
        </div>
    );
}
